import React, { ChangeEvent } from 'react'
import { AdvancedSearchView } from '@easyquery/ui'
import '@easyquery/enterprise'
import { Helmet } from 'react-helmet'
import ResponsiveAppBar from 'src/common/ResponsiveAppBar'
import { PageviewOutlined } from '@mui/icons-material'
import {
  Alert,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  OutlinedInput,
  Paper,
  Select,
  Snackbar,
  TextField,
  Typography,
  useTheme,
  Theme,
  Dialog,
} from '@mui/material'
import '../EasyQuery.css'
import { GetAdvancedSearchOptions } from 'src/services/LookupService'
import { AdvancedSearchConfigurationDTO } from 'src/viewModels/AdvancedSearchConfigurationDTO'
import {
  deleteAdvancedSearchConfiguration,
  getAdvancedSearchConfigurationById,
  insertAdvancedSearchConfiguration,
  updateAdvancedSearchConfiguration,
} from 'src/services/AdvancedSearchConfigurationService'
import { useParams } from 'react-router-dom'
import stores from 'src/Stores'

interface IEasyQueryProps {
  currentUserId: string
  theme: Theme
}

export class EasyQuery extends React.Component<IEasyQueryProps> {
  public state = {
    selectedReportId: '',
    newReportName: '',
    isOrganizationSurgical: stores.global.isCurrentOrganizationSurgical,
    organizationId: stores.global.currentAppOrganization!.id,
    isModalOpen: false,
    isDeletePromptOpen: false,
    snackbarOpen: false,
    snackbarMessage: '',
    snackbarSeverity: 'info' as 'success' | 'info' | 'warning' | 'error',
  }

  public easyQueryURL = `${
    process.env.REACT_APP_BASE_URL || 'http://localhost:5000'
  }/api/easyquery`
  public view = new AdvancedSearchView()
  public reports = new Array<AdvancedSearchConfigurationDTO>()

  public async componentDidMount() {
    const viewOptions = {
      enableExport: true,
      serverExporters: ['pdf', 'excel', 'csv'],
      loadModelOnStart: true,
      defaultModelId: this.state.isOrganizationSurgical
        ? 'eq_AdvancedSearch'
        : 'eq_Medical_AdvancedSearch',
      loadQueryOnStart: false,
      useBootstrap: true,
      handlers: {
        beforeFetchData: function (_context: any, requestData?: any) {
          if (!requestData) return
          requestData.options = requestData.options || {}
          requestData.options.sqlOptions = {
            selectDistinct: true,
          }
        },
        beforeExportResult: function (_context: any, requestData?: any) {
          if (!requestData) return
          requestData.options = requestData.options || {}
          requestData.options.sqlOptions = {
            selectDistinct: true,
          }
        },
      },
      widgets: {
        queryPanel: {
          showCheckboxes: false,
          showPoweredBy: false,
          showRootRow: true,
          showAddRow: true,
          autoEditNewCondition: true,
          sortEntities: true,
          accentActiveCondition: true,
          attrElementFormat: '{entity} > {attr}',
          titleElementFormat: '{attr}',
        },
        columnsBar: {
          accentActiveColumn: false,
          allowAggrColumns: false,
          showColumnCaptions: true,
          adjustEntitiesMenuHeight: false,
          sortEntities: true,
          menuOptions: {
            showSearchBoxAfter: 30,
            activateOnMouseOver: true,
          },
        },
        easyGrid: {
          paging: {
            enabled: true,
            pageSize: 20,
          },
        },
      },
      result: {
        showChart: false,
      },
    }

    this.view = new AdvancedSearchView()
    var context = this.view.getContext()

    context.useEndpoint(this.easyQueryURL).useEnterprise(() => {
      this.view.init(viewOptions)
    })

    context.addEventListener('ready', async () => {
      await GetAdvancedSearchOptions(this.state.organizationId).then((response) => {
        this.save = this.save.bind(this)
        this.updateReport = this.updateReport.bind(this)
        this.openDeleteReportModal = this.openDeleteReportModal.bind(this)
        this.deleteReport = this.deleteReport.bind(this)
        this.reloadAfterSave = this.reloadAfterSave.bind(this)
        this.addReport = this.addReport.bind(this)
        this.closeModal = this.closeModal.bind(this)
        this.closeDeletePrompt = this.closeDeletePrompt.bind(this)

        this.loadReportDropdownOptions(response)
      })
    })

    context.addEventListener('error', (_ctx, status) => {
      let msg = status?.sourceError?.message || 'An error occurred.'

      if (msg && msg.includes('Cannot find a path between tables')) {
        msg = 'Error: Criteria is not valid with selected report type.'
      } else if (msg && msg.includes('Error: 42601: syntax error at or near ")"')) {
        msg =
          'Error: Invalid criteria. If using "In List" or "Not In List" operator, please ensure you have select choices and clicked Apply.'
      }
      this.triggerAlert(msg)
    })
  }

  public loadQueryFromLocalStorage(selectedReport: string) {
    if (selectedReport) {
      this.view.getContext().clearQuery()
      let query = this.view.getContext().getQuery()
      query.loadFromDataOrJson(selectedReport)
      const report = this.reports.find((x) => x.advanceSearchQueryJson === selectedReport)
      if (report) {
        query.setName(report.name)
      }
      this.view.getContext().refreshWidgets()
      this.view.syncQuery()
    }
  }

  public clearSearch = () => {
    this.view.getContext().clearQuery()
    this.loadQueryFromLocalStorage(
      this.reports.find((x) => x.id == this.state.selectedReportId)
        ?.advanceSearchQueryJson ?? ''
    )
    this.clearAlert()
  }

  public clearAlert = () => {
    this.setState({ snackbarOpen: false })
  }

  public triggerAlert = (
    message: string,
    severity?: 'success' | 'info' | 'warning' | 'error'
  ) => {
    const isError = message.toLowerCase().includes('error')
    this.setState({
      snackbarOpen: true,
      snackbarMessage: message,
      snackbarSeverity: severity || (isError ? 'error' : 'info'),
    })
  }

  private setReportType = async (event: ChangeEvent<HTMLSelectElement>) => {
    this.setState({ selectedReportId: event.target.value })
    var report = this.reports.find((x) => x.id == event.target.value)

    if (report != undefined && report.advanceSearchQueryJson) {
      this.loadQueryFromLocalStorage(report.advanceSearchQueryJson)
      this.enableButton(!report.isGlobal, 'updateButton', this.updateReport)
      this.enableButton(
        !report.isGlobal,
        'deleteReportButton',
        this.openDeleteReportModal
      )
    } else {
      await getAdvancedSearchConfigurationById(event.target.value).then((response) => {
        this.reports.push(response)
        this.loadQueryFromLocalStorage(response.advanceSearchQueryJson)
        this.enableButton(!response.isGlobal, 'updateButton', this.updateReport)
        this.enableButton(
          !response.isGlobal,
          'deleteReportButton',
          this.openDeleteReportModal
        )
      })
    }
  }

  private openDeleteReportModal = () => {
    this.setState({ isDeletePromptOpen: true })
  }

  private async updateReport() {
    this.triggerAlert('Updating report...')
    this.enableButton(false, 'updateButton')
    var report = this.reports.find((x) => x.id == this.state.selectedReportId)

    if (report) {
      var data = report
      data.advanceSearchQueryJson = this.view.getContext().getQuery().toJSON()
      await updateAdvancedSearchConfiguration(report.id, data)
        .then(() => {
          report!.advanceSearchQueryJson = data.advanceSearchQueryJson
          this.clearAlert()
        })
        .catch(() => this.clearAlert())
    }
  }

  private addReport = () => {
    this.setState({ isModalOpen: true })
  }

  private loadReportDropdownOptions(
    response: AdvancedSearchConfigurationDTO[],
    selectedReportName?: string | undefined
  ) {
    if (!selectedReportName && response.length > 0) {
      selectedReportName = response[0].name
    }
    var firstReport = response.find((x) => x.name == selectedReportName)

    var line = new AdvancedSearchConfigurationDTO()
    line.id = 'line'
    line.name = '─────────────────────────'

    var dropdownElement = document.getElementById('reportType') as HTMLSelectElement
    if (!dropdownElement) {
      return
    }
    dropdownElement.options.length = 0

    var globalReports = response.filter((x) => x.isGlobal == true)
    var userReports = response.filter((x) => x.isGlobal == false)
    var ordered =
      userReports.length > 0
        ? globalReports.concat(line).concat(userReports)
        : globalReports

    ordered.forEach((report) => {
      var optionElement = document.createElement('option')
      optionElement.value = report.id
      optionElement.innerText = report.name

      if (report.id == 'line') {
        optionElement.disabled = true
      }

      if (report.name == selectedReportName) {
        optionElement.selected = true
      }

      dropdownElement?.append(optionElement)

      if (report.advanceSearchQueryJson != '') {
        this.reports.push(report)
      }
    })

    this.setState({
      selectedReportId: firstReport?.id,
    })

    firstReport && this.loadQueryFromLocalStorage(firstReport?.advanceSearchQueryJson)

    return firstReport
  }

  public async reloadAfterSave() {
    await GetAdvancedSearchOptions(this.state.organizationId)
      .then((response) => {
        var newReport = this.loadReportDropdownOptions(response, this.state.newReportName)
        this.enableButton(!newReport?.isGlobal, 'updateButton', this.updateReport)
        this.enableButton(
          !newReport?.isGlobal,
          'deleteReportButton',
          this.openDeleteReportModal
        )

        this.clearAlert()
        this.closeModal()
      })
      .catch(() => {
        location.reload()
      })
  }

  public async save() {
    this.triggerAlert('Saving report...')
    this.enableButton(false, 'saveButton')
    var report = this.reports.find((x) => x.id == this.state.selectedReportId)

    if (report) {
      var data = report
      data.name = this.state.newReportName
      data.advanceSearchQueryJson = this.view.getContext().getQuery().toJSON()
      data.organizationId = this.state.organizationId

      if (data.name == '') {
        this.enableButton(true, 'saveButton')
        return
      } else {
        insertAdvancedSearchConfiguration(data)
          .then(() => {
            this.reloadAfterSave()
          })
          .catch(() => this.clearAlert())
      }
    }
  }

  public deleteReport() {
    var report = this.reports.find((x) => x.id == this.state.selectedReportId)

    if (report) {
      var data = report

      deleteAdvancedSearchConfiguration(data.id)
        .then(() => {
          window.location.reload()
        })
        .catch(() => this.clearAlert())
    }
  }

  private closeDeletePrompt = () => {
    this.setState({ isDeletePromptOpen: false })
  }

  private closeModal = () => {
    this.setState({ isModalOpen: false, newReportName: '' })
    var textFieldElement = document.getElementById('newReportName') as HTMLTextAreaElement
    if (textFieldElement) {
      textFieldElement.value = ''
    }
  }

  private enableButton(
    enable: boolean,
    buttonId: string,
    onClick?: () => Promise<void> | void
  ) {
    var buttonElement = document.getElementById(buttonId) as HTMLButtonElement

    if (buttonElement) {
      if (onClick) {
        buttonElement.onclick = () => onClick()
      }
      if (enable) {
        buttonElement.ariaDisabled = 'false'
        buttonElement.disabled = false
        buttonElement.style.backgroundColor =
          buttonId == 'deleteReportButton'
            ? this.state.isOrganizationSurgical
              ? '#8bb84b'
              : '#90C5B6'
            : this.state.isOrganizationSurgical
              ? '#29348F'
              : '#708BC3'
        buttonElement.style.color = buttonId == 'deleteReportButton' ? 'black' : 'white'
        buttonElement.style.cursor = 'pointer'
      } else {
        buttonElement.ariaDisabled = 'true'
        buttonElement.disabled = true
        buttonElement.style.backgroundColor = 'rgba(170, 170, 170, 0.3)'
        buttonElement.style.color = 'rgba(0, 0, 0, 0.26)'
        buttonElement.style.cursor = 'default'
      }
    }
  }

  public renderModal() {
    return (
      <>
        <Dialog
          open={this.state.isModalOpen}
          fullWidth={true}
          maxWidth="md"
          onClose={() => this.closeModal()}
        >
          <DialogTitle style={{ padding: 0 }}>
            <ResponsiveAppBar title={'Add Report'} isModal={true} />
          </DialogTitle>
          <DialogContent style={{ padding: '12px 12px 8px' }}>
            <Grid container direction="column" spacing={1}>
              <Grid item xs={12}>
                <TextField
                  label="Report Name"
                  name="newReportName"
                  id="newReportName"
                  variant="outlined"
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  required={true}
                  onChange={(event) => {
                    this.setState({ newReportName: event.target.value })
                    this.enableButton(event.target.value != '', 'saveButton', this.save)
                  }}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button
              id="saveButton"
              disabled
              data-cy="save"
              style={{
                pointerEvents: 'auto',
                backgroundColor: 'rgba(170, 170, 170, 0.3)',
                color: 'rgba(0, 0, 0, 0.26)',
              }}
            >
              Save
            </Button>
            <Button
              onClick={() => {
                this.setState({ newReportName: '' })
                this.enableButton(false, 'saveButton', this.save)
                this.closeModal()
              }}
              data-cy="close"
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>
      </>
    )
  }

  public renderDeletePrompt() {
    return (
      <>
        <Dialog
          open={this.state.isDeletePromptOpen}
          fullWidth={true}
          maxWidth="md"
          onClose={() => this.closeDeletePrompt()}
        >
          <DialogTitle style={{ padding: 0 }}>
            <ResponsiveAppBar title={'Delete Report'} isModal={true} />
          </DialogTitle>
          <DialogContent style={{ padding: '12px 12px 8px' }}>
            <Grid container direction="column" spacing={1}>
              <Grid item xs={12}>
                <Typography>Are you sure you want to delete?</Typography>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button
              id="deleteButton"
              data-cy="delete"
              onClick={() => {
                this.triggerAlert('Deleting report...')
                this.enableButton(false, 'deleteButton')
                this.deleteReport()
              }}
              style={{
                pointerEvents: 'auto',
                backgroundColor: '#d32f2f',
                color: 'white',
              }}
            >
              Delete
            </Button>
            <Button onClick={this.closeDeletePrompt} data-cy="close">
              Close
            </Button>
          </DialogActions>
        </Dialog>
      </>
    )
  }

  public render() {
    return (
      <>
        <Helmet>
          <title>Advanced Search</title>
          <style type="text/css">
            {`
              .eqjs-cb-row-column-entattr {
                background: #f5f5f5 !important;
              }
              .eqjs-qp-row:hover {
                background-color: #e0e0e0 !important;
              }
            `}
          </style>
        </Helmet>
        <ResponsiveAppBar title="Advanced Search" pageIcon={<PageviewOutlined />} />
        {this.renderModal()}
        {this.renderDeletePrompt()}
        <Snackbar
          open={this.state.snackbarOpen}
          autoHideDuration={6000}
          onClose={this.clearAlert}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <Alert
            onClose={this.clearAlert}
            severity={this.state.snackbarSeverity}
            sx={{
              width: '100%',
              ...(this.state.snackbarSeverity === 'error' && {
                backgroundColor: '#f44336',
                color: '#fff',
                '& .MuiAlert-icon': {
                  color: '#fff',
                },
              }),
            }}
          >
            {this.state.snackbarMessage}
          </Alert>
        </Snackbar>
        <Grid>
          <Grid container item style={{ paddingLeft: '20px' }} xs={12}>
            <Grid
              container
              direction="column"
              md={3}
              xs={12}
              style={{ paddingTop: '25px', paddingRight: '10px' }}
            >
              <FormControl fullWidth variant="outlined">
                <InputLabel shrink={true} htmlFor="report-type-select" variant="outlined">
                  Report Type
                </InputLabel>
                <Select
                  fullWidth
                  id="reportType"
                  variant="outlined"
                  native={true}
                  onChange={this.setReportType as any}
                  input={
                    <OutlinedInput
                      id="report-type-select"
                      name="reportType"
                      notched
                      label="Report Type"
                    />
                  }
                  inputProps={{ label: true, notched: true }}
                ></Select>
              </FormControl>
              <Grid
                container
                direction="row"
                style={{
                  justifyContent: 'flex-end',
                  paddingTop: '8px',
                  paddingBottom: '30px',
                }}
              >
                <Grid item>
                  <Button
                    id="deleteReportButton"
                    size="small"
                    variant="contained"
                    onClick={this.openDeleteReportModal}
                    style={{ pointerEvents: 'auto' }}
                    disabled
                  >
                    Delete
                  </Button>
                </Grid>
                <Grid item style={{ paddingLeft: '8px' }}>
                  <Button
                    id="updateButton"
                    color="primary"
                    size="small"
                    variant="contained"
                    onClick={this.updateReport}
                    style={{ pointerEvents: 'auto' }}
                    disabled
                  >
                    Update
                  </Button>
                </Grid>
                <Grid item style={{ paddingLeft: '8px' }}>
                  <Button
                    color="primary"
                    size="small"
                    variant="contained"
                    style={{ boxShadow: 'none' }}
                    onClick={this.addReport}
                  >
                    Add Report
                  </Button>
                </Grid>
              </Grid>
            </Grid>

            {/* Enable this for when you need to see configured entities for easy query model */}
            {/* <Grid container direction="column" md={4}>
                            <div id="EntitiesPanel"></div>
                        </Grid> */}

            <Grid container direction="column" md={8} xs={12}>
              <div
                style={{
                  background:
                    this.props.theme?.palette.mode === 'dark' ? '#121212' : 'transparent',
                }}
                id="ColumnsBar"
              ></div>
            </Grid>
          </Grid>
          <Grid container item style={{ paddingLeft: '20px' }}>
            <Paper
              elevation={2}
              style={{
                padding: '20px',
                marginTop: '20px',
                marginRight: '20px',
                width: '100%',
              }}
            >
              <Grid
                style={{
                  paddingBottom: '10px',
                  background:
                    this.props.theme?.palette.mode === 'dark'
                      ? '#424242 !important'
                      : 'transparent',
                  width: '100%',
                }}
              >
                <Typography
                  style={{ paddingLeft: '10px' }}
                  display="inline"
                  color="textSecondary"
                  variant="body2"
                >
                  SEARCH CRITERIA
                </Typography>
                <div
                  id="QueryPanel"
                  style={{
                    background:
                      this.props.theme?.palette.mode === 'dark'
                        ? '#121212 !important'
                        : 'transparent',
                  }}
                ></div>
              </Grid>
              <Grid
                container
                direction="row"
                style={{
                  marginLeft: '10px',
                  marginTop: '5px',
                  marginBottom: '10px',
                  background:
                    this.props.theme?.palette.mode === 'dark' ? '#121212' : 'transparent',
                }}
              >
                <span className="eqv-export-buttons" style={{ width: '100%' }}>
                  <Grid container style={{ justifyContent: 'space-between' }}>
                    <Grid item>
                      <Button
                        id="FetchDataButton"
                        color="primary"
                        size="medium"
                        variant="contained"
                        style={{ marginRight: '10px' }}
                        onClick={this.clearAlert}
                      >
                        Search
                      </Button>
                      <Button
                        onClick={this.clearSearch}
                        color="secondary"
                        size="medium"
                        variant="contained"
                        style={{ marginRight: '10px' }}
                      >
                        Clear Search
                      </Button>
                    </Grid>
                    <Grid item style={{ marginRight: '20px' }}>
                      <Button
                        size="medium"
                        variant="contained"
                        className="eqjs-export"
                        onClick={() =>
                          this.triggerAlert(
                            'Please leave the tab open while your data is exported.'
                          )
                        }
                        data-format="excel"
                      >
                        Export to Excel
                      </Button>
                    </Grid>
                  </Grid>
                </span>
              </Grid>
              <div
                id="eqv-content"
                style={{
                  background:
                    this.props.theme?.palette.mode === 'dark'
                      ? '#121212 !important'
                      : 'transparent',
                }}
              >
                <div className="eqv-bottom-panel">
                  <div
                    className="eqv-result-panel"
                    style={{
                      width: '100%',
                      background:
                        this.props.theme?.palette.mode === 'dark'
                          ? '#121212 !important'
                          : 'transparent',
                    }}
                  >
                    <hr
                      className="eqv-result-panel-hr eqv-hr"
                      style={{ display: 'none' }}
                    />
                    <div
                      className="eqv-result-panel-title"
                      style={{
                        background:
                          this.props.theme?.palette.mode === 'dark'
                            ? '#121212 !important'
                            : 'transparent',
                      }}
                    >
                      Result
                      <span
                        id="ResultCount"
                        style={{
                          display: 'none',
                          marginLeft: 20,
                          fontSize: 'small',
                        }}
                      ></span>
                    </div>
                    <div
                      id="ResultPanel"
                      className="eqv-result-panel-content"
                      style={{
                        background:
                          this.props.theme?.palette.mode === 'dark'
                            ? '#121212 !important'
                            : 'transparent',
                      }}
                    ></div>
                  </div>
                </div>
              </div>
            </Paper>
          </Grid>
        </Grid>
      </>
    )
  }

  //This disables rerenders
  public shouldComponentUpdate(_nextProps: IEasyQueryProps, nextState: any) {
    if (
      this.state.isModalOpen !== nextState.isModalOpen ||
      this.state.isDeletePromptOpen !== nextState.isDeletePromptOpen ||
      this.state.snackbarOpen !== nextState.snackbarOpen
    ) {
      return true
    }
    return false
  }
}

const EasyQueryWithRouter = () => {
  const { currentUserId } = useParams<{ currentUserId: string }>()
  const theme = useTheme()
  return <EasyQuery currentUserId={currentUserId!} theme={theme} />
}

export default EasyQueryWithRouter
